import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Send, Trash2, MapPin, Phone, Mail, Home, User, CheckCircle2, ChevronLeft, Star, BadgeCheck, ExternalLink } from 'lucide-react'
import { useChatSession, type ChatTurn } from '../../hooks/useChatSession'
import { SUPPORT_EMAIL } from '../../lib/contact'
import { ASSISTANT_NAME, ASSISTANT_AVATAR_SRC } from '../../lib/persona'
import { createJob } from '../../lib/api/jobs'
import { getApiErrorMessage } from '../../lib/api'
import { saveSession, loadSession } from '../../hooks/useFormSession'
import { JOB_CATEGORIES, buildIssueDescription } from '../../lib/jobCategories'
import { logStatsigEvent } from '../../lib/statsig'
import type { BookingDraft, PlumberResultCard } from '../../lib/api/chat'

/**
 * One-tap openers shown under the greeting (issue #78).
 *
 * The greeting asks what the emergency is, and for someone mid-emergency a
 * tap is easier than composing a message. Each chip sends a full sentence, not
 * the bare category label: create_emergency_request needs a description of at
 * least one full sentence, so a bare "Boiler / heating" would stall at the
 * drafting step. These are deliberately broad. They answer "what kind of
 * problem" and let the model carry on to the postcode; the category picker
 * still follows for specifics (see systemPrompt.ts).
 *
 * Plain text, no emoji, matching the assistant's own voice. The fourth option
 * sends nothing: it just focuses the input, for anyone whose problem is not on
 * the list.
 */
const QUICK_STARTS: { id: string; label: string; message: string | null }[] = [
  { id: 'leak',    label: 'Burst or leaking pipe',  message: 'I have a burst or leaking pipe.' },
  { id: 'boiler',  label: 'Boiler or heating down', message: 'My boiler or heating has stopped working.' },
  { id: 'drain',   label: 'Blocked drain or toilet', message: 'I have a blocked drain or toilet.' },
  { id: 'other',   label: 'Something else',         message: null },
]

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
    </span>
  )
}

/** The category-picker's synthesized description already starts with the
 *  category label (e.g. "Boiler / heating - No heating..."), and this card
 *  also shows that same label separately next to its emoji - concatenating
 *  both verbatim would print the label twice ("Boiler / heating - Boiler /
 *  heating - ..."). Strip a leading duplicate of the label (case-insensitive)
 *  when present; a freeform description that never went through the picker
 *  is left completely untouched. */
function stripLeadingCategoryLabel(label: string, description: string): string {
  if (!label) return description
  const prefix = `${label} - `
  return description.toLowerCase().startsWith(prefix.toLowerCase()) ? description.slice(prefix.length) : description
}

/**
 * Renders what create_emergency_request (tools.ts) drafted. Nothing real has
 * happened yet - the tool never calls createJob() itself. Confirm here calls
 * the exact same createJob() EmergencyForm.tsx uses, writes to the same
 * fd_form_session key, and hands off to the real job page - no parallel
 * booking/selection UI to build or keep in sync.
 */
function BookingConfirmCard({ draft, onSubmitted }: { draft: BookingDraft; onSubmitted: (note: string) => void }) {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const category = JOB_CATEGORIES.find(c => c.id === draft.category)
  const categoryLabel = category ? `${category.emoji} ${category.label}` : draft.category
  const displayDescription = stripLeadingCategoryLabel(category?.label ?? '', draft.issueDescription)

  async function handleConfirm() {
    setSubmitting(true)
    setError(null)
    try {
      const { jobId, jobSecret, jobRef, status } = await createJob({
        phone: draft.phone,
        email: draft.email,
        name: draft.name,
        postcode: draft.postcode,
        issueDescription: draft.issueDescription,
        category: draft.category,
        address: draft.address,
      })
      saveSession({
        ...loadSession(),
        postcode: draft.postcode,
        category: draft.category,
        phone: draft.phone,
        email: draft.email,
        address: draft.address,
        name: draft.name,
        jobId, jobSecret, jobRef,
        step: 5,
      })
      // Same funnel event EmergencyForm.tsx logs for a form booking, tagged
      // source: 'chat' - without this, every chat-originated booking was
      // completely invisible to the booking_job_created/booking_quote_selected
      // funnel Statsig already tracks (see statsig.ts). paywallVariant matches
      // the tagging every other funnel event in this session got - see
      // EmergencyForm.tsx's own booking_job_created call for the same derivation.
      logStatsigEvent('booking_job_created', jobId, {
        category: draft.category,
        source: 'chat',
        paywallVariant: status === 'AWAITING_PAYMENT' ? 'paywall_on' : 'paywall_off',
      })
      setSubmitted(true)
      // The card visibly flips to "Submitted" for the customer, but the
      // model has no way to know that on its own - its whole world is the
      // `messages` array. Without this, it keeps telling the customer to
      // click Confirm even after they already have (and it worked) - this
      // note is what lets every later reply in the session know it's done.
      onSubmitted(`[System note] The emergency request was just submitted successfully - job reference ${jobRef}, job ID ${jobId}. A plumber search is now underway. Do not ask the customer to click Confirm again for this request; if they thank you or say hi, acknowledge it's already in progress. If they ask for an update, you can check the real status with get_job_status using this job ID - don't guess at it.`)
      navigate(`/job/${jobId}`)
    } catch (err) {
      const message = getApiErrorMessage(err, 'Could not submit your request. Please try again.')
      setError(message)
      logStatsigEvent('booking_job_create_failed', undefined, { message, source: 'chat' })
      onSubmitted(`[System note] The customer just clicked Confirm but the submission failed: "${message}". Nothing was created - it's still fine to ask them to try clicking Confirm again, or to fix whatever the error mentions.`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-2 bg-white border-2 border-gold-400 rounded-2xl p-4 max-w-[85%] shadow-sm">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Ready to submit</p>
      <div className="space-y-1.5 text-sm text-gray-700 mb-3">
        {draft.name && <p className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />{draft.name}</p>}
        <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />{draft.postcode}{draft.address ? ` · ${draft.address}` : ''}</p>
        <p className="flex items-start gap-2"><span className="w-3.5 flex-shrink-0 text-center leading-5">{category?.emoji ?? '🔧'}</span><span>{categoryLabel} - {displayDescription}</span></p>
        <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />{draft.phone}</p>
        {draft.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />{draft.email}</p>}
        {!draft.address && <p className="flex items-center gap-2 text-amber-600"><Home className="h-3.5 w-3.5 flex-shrink-0" />No address yet - you'll be asked on the next page</p>}
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {submitted ? (
        <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Submitted - taking you there…</p>
      ) : (
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-emergency-500 hover:bg-emergency-600 disabled:opacity-60 text-white font-bold px-4 py-2.5 rounded-xl transition-colors text-sm"
        >
          {submitting ? 'Submitting…' : 'Confirm & search for plumbers'}
        </button>
      )}
    </div>
  )
}

/**
 * Shown when the model calls show_category_picker (tools.ts) - the same
 * issue-type chips + follow-up questions EmergencyForm.tsx's step 1 uses, so
 * a chat booking asks exactly the same questions a form booking would rather
 * than relying on the model to freeform-extract that detail from prose. The
 * customer's picks get sent back as a normal chat message once they're done,
 * worded the same way buildDescription() would - the model just carries it
 * straight into issueDescription, it never needs to be re-typed or rephrased.
 */
function CategoryPickerCard({ onSubmit, disabled }: { onSubmit: (text: string) => void; disabled: boolean }) {
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [subSelections, setSubSelections] = useState<string[]>([])
  const [followUpValues, setFollowUpValues] = useState<Record<string, string>>({})
  const [otherText, setOtherText] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const category = JOB_CATEGORIES.find(c => c.id === categoryId)
  const activeFollowUps = (category?.subOptions ?? []).filter(o => o.followUp && subSelections.includes(o.id))

  const canContinue = useMemo(() => {
    if (!category) return false
    if (category.id === 'other') return otherText.trim().length > 0
    if (!category.subOptions?.length) return true
    if (subSelections.length === 0) return false
    return subSelections.every(id => {
      const opt = category.subOptions!.find(o => o.id === id)
      return !(opt?.followUp?.required && !followUpValues[id]?.trim())
    })
  }, [category, subSelections, followUpValues, otherText])

  function pickCategory(id: string) {
    setCategoryId(id)
    setSubSelections([])
    setFollowUpValues({})
    setOtherText('')
  }

  function toggleSub(id: string) {
    if (!category) return
    if (category.multiSelect) {
      setSubSelections(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]))
    } else {
      setSubSelections([id])
    }
  }

  function handleContinue() {
    if (!category || !canContinue) return
    onSubmit(buildIssueDescription({ categoryId: category.id, subSelections, followUpValues, otherText }))
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400 max-w-[85%]">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" /> Sent
      </div>
    )
  }

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded-2xl p-3.5 max-w-[90%] shadow-sm">
      {!category ? (
        <>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">What's the issue?</p>
          <div className="grid grid-cols-2 gap-1.5">
            {JOB_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                disabled={disabled}
                onClick={() => pickCategory(cat.id)}
                className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl border border-gray-200 text-left text-xs font-medium text-gray-700 hover:border-gold-400 hover:bg-gold-50 disabled:opacity-50 transition-colors"
              >
                <span className="text-sm leading-none flex-shrink-0">{cat.emoji}</span>
                <span className="leading-snug">{cat.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setCategoryId(null)}
            className="inline-flex items-center gap-0.5 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-2.5"
          >
            <ChevronLeft className="h-3 w-3" /> {category.emoji} {category.label}
          </button>

          {category.id === 'other' ? (
            <textarea
              rows={3}
              autoFocus
              value={otherText}
              onChange={e => setOtherText(e.target.value)}
              placeholder="What's going on?"
              className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-100 focus:border-gold-400 transition-colors resize-none"
            />
          ) : (
            <>
              {category.subQuestion && <p className="text-xs font-semibold text-gray-600 mb-2">{category.subQuestion}</p>}
              <div className="flex flex-wrap gap-1.5">
                {(category.subOptions ?? []).map(opt => {
                  const selected = subSelections.includes(opt.id)
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleSub(opt.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs transition-colors disabled:opacity-50 ${
                        selected
                          ? 'border-gold-400 bg-gold-50 text-gold-800 font-semibold'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="leading-none">{opt.emoji}</span>
                      <span>{opt.label}</span>
                    </button>
                  )
                })}
              </div>
              {activeFollowUps.length > 0 && (
                <div className="space-y-2 mt-2.5">
                  {activeFollowUps.map(opt => (
                    <input
                      key={opt.id}
                      type="text"
                      value={followUpValues[opt.id] ?? ''}
                      onChange={e => setFollowUpValues(prev => ({ ...prev, [opt.id]: e.target.value }))}
                      placeholder={opt.followUp!.placeholder}
                      className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-100 focus:border-gold-400 transition-colors"
                    />
                  ))}
                </div>
              )}
            </>
          )}

          <button
            type="button"
            disabled={disabled || !canContinue}
            onClick={handleContinue}
            className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-emergency-500 hover:bg-emergency-600 disabled:opacity-40 text-white font-semibold px-3 py-2 rounded-xl transition-colors text-xs"
          >
            Continue
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Renders what find_plumbers_nearby (tools.ts) found - real, clickable
 * profile links, never text the model typed out itself (the system prompt
 * tells it not to try). Each link is a plain in-app path built from
 * directorySlug (never a raw URL handed over the wire - see tools.ts's
 * ToolCallResult comment), opened in a new tab so browsing plumbers never
 * navigates away from - or loses - the conversation still open behind it.
 * No phone/email anywhere here; the server never even selects those fields
 * out of the database for this tool in the first place.
 */
function PlumberResultsCard({ results }: { results: PlumberResultCard[] }) {
  return (
    <div className="mt-2 bg-white border border-gray-200 rounded-2xl p-3.5 max-w-[90%] shadow-sm space-y-2">
      {results.map(p => (
        <a
          key={p.directorySlug}
          href={`/plumbers/find/${p.directorySlug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-2.5 py-2 hover:border-gold-400 hover:bg-gold-50 transition-colors group"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-xs font-semibold text-gray-900 truncate">{p.companyName}</p>
              {p.gasSafe && (
                <span title="Gas Safe registered" className="flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-full flex-shrink-0">
                  <BadgeCheck className="h-2.5 w-2.5" />
                  Gas Safe
                </span>
              )}
              {p.availableNow && (
                <span className="flex items-center gap-1 text-[9px] font-semibold text-emerald-600 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Available now
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
              {p.googleRating != null && (
                <span className="flex items-center gap-0.5">
                  <Star className="h-3 w-3 fill-gold-500 text-gold-500" />
                  <span className="font-medium text-gray-700">{p.googleRating.toFixed(1)}</span>
                  {p.googleReviewCount != null && p.googleReviewCount > 0 && (
                    <span className="text-gray-400">({p.googleReviewCount})</span>
                  )}
                </span>
              )}
              {(p.city || p.postcodeArea) && <span className="truncate">{p.city || p.postcodeArea}</span>}
            </div>
          </div>
          <ExternalLink className="h-3.5 w-3.5 text-gray-300 group-hover:text-gold-600 flex-shrink-0 transition-colors" />
        </a>
      ))}
    </div>
  )
}

export function ChatPanel({ onClose }: { onClose: () => void }) {
  const { messages, isStreaming, error, sendMessage, clearChat, recordSystemNote } = useChatSession()
  const [draft, setDraft] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, error])

  useEffect(() => { inputRef.current?.focus() }, [])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!draft.trim() || isStreaming) return
    void sendMessage(draft)
    setDraft('')
  }

  // True only while we're waiting on the very first chunk of a reply -
  // after that there's real streamed text to show instead of a spinner.
  const waitingForFirstChunk = isStreaming && messages[messages.length - 1]?.content === ''

  // Quick-start chips are an opener, not a permanent fixture: offered only while
  // the customer has not said anything yet. Checked against the whole history
  // rather than "is this the first render", so a restored session that already
  // has a conversation in it never gets them back.
  const showQuickStarts = !messages.some(m => m.role === 'user') && !isStreaming

  // Hidden turns (system notes) never render, so avatar run-grouping has to be
  // worked out over what's actually on screen. Grouping over the raw array
  // would let an invisible turn sitting between two assistant replies restart
  // the run and repeat the face for no reason the customer can see.
  // `key` stays the index into `messages`, not into this list, so a turn keeps
  // a stable React key as hidden notes are appended around it.
  const visibleMessages = useMemo(() => {
    const out: { turn: ChatTurn; key: number; startsAssistantRun: boolean }[] = []
    messages.forEach((turn, key) => {
      if (turn.hidden) return
      const prev = out[out.length - 1]?.turn
      const startsAssistantRun = turn.role === 'assistant'
        && (!prev || prev.role !== 'assistant' || Boolean(prev.fromHuman) !== Boolean(turn.fromHuman))
      out.push({ turn, key, startsAssistantRun })
    })
    return out
  }, [messages])

  return (
    <div
      role="dialog"
      aria-label="FixDelivered chat assistant"
      className="flex flex-col w-[calc(100vw-2rem)] max-w-sm h-[min(32rem,70vh)] bg-white rounded-2xl ring-1 ring-gray-900/[0.08] chat-panel-shadow overflow-hidden"
    >
      {/* Header. No light bottom border here - the panel is dark at the top and
          white below, so the colour change is already the divider; a gray-100
          line on gray-900 just reads as a seam. */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-900 text-white">
        <div className="flex items-center gap-2.5 min-w-0">
          {/* Explicit width/height matching the rendered box - this header is
              built on every chat open, and an image without intrinsic size
              would reflow the name beside it as it loads. */}
          <img
            src={ASSISTANT_AVATAR_SRC}
            alt=""
            aria-hidden="true"
            width={36}
            height={36}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-gold-500 ring-1 ring-white/15"
          />
          <div className="min-w-0 leading-tight">
            {/* The name is the accessible label for the avatar too, which is
                why the image itself is alt="" - announcing it separately would
                just read the same thing twice. Negative tracking because
                semibold at this size sets slightly loose. */}
            <p className="text-sm font-semibold tracking-[-0.01em] truncate">{ASSISTANT_NAME}</p>
            <p className="mt-0.5 text-xs text-gray-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
              Online now
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={clearChat}
              title="Start a new conversation"
              aria-label="Start a new conversation"
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      {/* aria-live so a streamed reply is actually announced - without it a
          screen-reader user gets silence while text appears on screen. polite,
          not assertive: it should wait for a pause rather than interrupt
          whatever the reader is already saying. */}
      <div
        ref={scrollRef}
        aria-live="polite"
        aria-atomic="false"
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-gray-50"
      >
        {visibleMessages.map(({ turn: m, key, startsAssistantRun }) => (
          <div key={key} className={`chat-msg-in flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
            {/* A real team member's Discord reply (issue #51), not the
                model - labelled distinctly so the customer can tell. */}
            {m.fromHuman && (
              <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide mb-1 ml-9">FixDelivered team</span>
            )}
            {/* items-end so the avatar sits on the baseline of the bubble
                rather than floating at the top of a long one - it reads as
                someone speaking, not as a bullet point. */}
            <div className={`flex items-end gap-2 w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                /* Only on the first message of a run: a column of identical
                   faces down the side of a threaded reply is noise. The
                   spacer keeps every continuation bubble on the same left
                   edge as the first. */
                startsAssistantRun ? (
                  <img
                    src={ASSISTANT_AVATAR_SRC}
                    alt=""
                    aria-hidden="true"
                    width={28}
                    height={28}
                    className="w-7 h-7 rounded-full object-cover flex-shrink-0 bg-gold-500 ring-1 ring-gray-900/[0.06]"
                  />
                ) : (
                  <div className="w-7 flex-shrink-0" aria-hidden="true" />
                )
              )}
              <div
                /* Assistant bubbles carry a hairline ring rather than a full
                   border - on a gray-50 ground a 1px gray-100 border reads as
                   a smudge, a ring at 6% black reads as an edge. */
                className={`max-w-[85%] px-4 py-2.5 text-[0.9375rem] leading-relaxed whitespace-pre-wrap break-words ${
                  m.role === 'user'
                    ? 'bg-gray-900 text-white rounded-2xl rounded-br-md shadow-sm'
                    : m.fromHuman
                      ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-600/20 rounded-2xl rounded-bl-md'
                      : 'bg-white text-gray-800 ring-1 ring-gray-900/[0.06] rounded-2xl rounded-bl-md shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
                }`}
              >
                {m.role === 'assistant' && m.content === '' && waitingForFirstChunk ? <TypingDots /> : m.content}
              </div>
            </div>
            {/* Cards sit under the bubble, inset to the same left edge so the
                whole assistant turn reads as one block. */}
            {(m.confirmBooking || m.showCategoryPicker || (m.plumberResults && m.plumberResults.length > 0)) && (
              <div className={m.role === 'assistant' ? 'ml-9 w-full' : 'w-full'}>
                {m.confirmBooking && <BookingConfirmCard draft={m.confirmBooking} onSubmitted={recordSystemNote} />}
                {m.showCategoryPicker && (
                  <CategoryPickerCard
                    disabled={isStreaming}
                    onSubmit={text => void sendMessage(text)}
                  />
                )}
                {m.plumberResults && m.plumberResults.length > 0 && <PlumberResultsCard results={m.plumberResults} />}
              </div>
            )}
          </div>
        ))}
        {showQuickStarts && (
          // Aligned with the assistant bubble above (avatar column is ml-9),
          // and gone the moment the customer sends anything, by tap or typing.
          <div role="group" aria-label="Quick replies" className="chat-msg-in flex flex-wrap gap-2 ml-9">
            {QUICK_STARTS.map(opt => (
              <button
                key={opt.id}
                type="button"
                disabled={isStreaming}
                onClick={() => {
                  logStatsigEvent('chat_quick_reply_clicked', opt.id)
                  if (opt.message) void sendMessage(opt.message)
                  else inputRef.current?.focus()
                }}
                className="px-3.5 py-2 rounded-full bg-white text-sm font-medium text-gray-800 ring-1 ring-gray-900/[0.12] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:ring-gold-500 hover:bg-gold-50 active:scale-[0.97] disabled:opacity-50 transition-[box-shadow,background-color,transform] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-xs">
            {error} Or email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-medium">{SUPPORT_EMAIL}</a>.
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 p-3 border-t border-gray-100 bg-white">
        <label htmlFor="fd-chat-input" className="sr-only">Type a message</label>
        <input
          id="fd-chat-input"
          ref={inputRef}
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Type a message…"
          maxLength={4000}
          disabled={isStreaming}
          /* Mobile keyboards: sentence case and an explicit Send key rather
             than a newline, since this submits on Enter anyway. 16px text
             (text-base) below sm because iOS Safari zooms the whole page in
             on focus for anything smaller, which is jarring mid-emergency. */
          autoComplete="off"
          autoCapitalize="sentences"
          enterKeyHint="send"
          className="flex-1 min-w-0 px-3.5 py-2.5 text-base sm:text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-xl transition-shadow focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-100 disabled:bg-gray-50 disabled:text-gray-400"
        />
        <button
          type="submit"
          disabled={isStreaming || !draft.trim()}
          aria-label="Send message"
          className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-900 hover:bg-black disabled:opacity-30 disabled:hover:bg-gray-900 text-white flex items-center justify-center transition-[background-color,transform] duration-150 active:scale-95 disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  )
}
